import React, { useState } from 'react';
import { useStudioTheme, motion, type StudioTokens } from '../studioTheme';
import {
  GlassCard, CopyButton, StatusDot, Divider,
  healthColor, healthLabel, healthToAnimation,
  knowledgeColor, knowledgeLabel, freshness,
} from '../studioPrimitives';
import type { CeoDecision, Workspace } from '../../../../data/agentCommandCenter';
import { generateContinueProtocol, generateSyncProtocol } from '../../../../data/studioProtocols';

// ── Decision item ─────────────────────────────────────────────────────────────

function DecisionItem({
  decision,
  isExpanded,
  onToggle,
}: {
  decision:   CeoDecision;
  isExpanded: boolean;
  onToggle:   () => void;
}) {
  const { ST } = useStudioTheme();
  const isHigh = decision.priority === 'high' || decision.priority === 'critical';

  return (
    <div
      onClick={onToggle}
      style={{
        padding:         `${ST.space[3]}px ${ST.space[4]}px`,
        borderRadius:    ST.radius.md,
        backgroundColor: isExpanded ? ST.bg.surfaceActive : 'transparent',
        border:          `1px solid ${isExpanded ? ST.border.accent : 'transparent'}`,
        cursor:          'pointer',
        transition:      motion(ST.motion.fast),
      }}
    >
      <div style={{
        display:        'flex',
        alignItems:     'flex-start',
        justifyContent: 'space-between',
        gap:            ST.space[2],
        marginBottom:   2,
      }}>
        <div style={{
          fontSize: 12, fontWeight: 500,
          color:    isExpanded ? ST.text.primary : ST.text.secondary,
          fontFamily: ST.font.sans, lineHeight: 1.45, flex: 1,
        }}>
          {decision.subject}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, marginTop: 2 }}>
          <div style={{
            width:           6,
            height:          6,
            borderRadius:    '50%',
            backgroundColor: isHigh ? ST.status.degraded : ST.status.needsSync,
            boxShadow:       `0 0 5px ${isHigh ? ST.status.degraded : ST.status.needsSync}80`,
          }} />
          <span style={{
            fontSize: 10, color: ST.text.disabled,
            transition: motion(ST.motion.fast),
            transform:  isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display:    'inline-block',
          }}>›</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans }}>{decision.from}</span>
        <span style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans }}>
          {freshness(decision.date).replace('Updated ', '')}
        </span>
      </div>
      {isExpanded && (
        <div style={{
          marginTop: ST.space[2], paddingTop: ST.space[2],
          borderTop: `1px solid ${ST.border.subtle}`,
          fontSize: 11, color: ST.text.muted, fontFamily: ST.font.sans, lineHeight: 1.65,
        }}>
          {decision.body}
        </div>
      )}
    </div>
  );
}

// ── Needs attention digest ────────────────────────────────────────────────────

const _PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

function BlockersDigest({ workspaces }: { workspaces: Workspace[] }) {
  const { ST } = useStudioTheme();
  const urgent = workspaces
    .filter(w => w.state.blockers.length > 0 || w.state.priority === 'critical')
    .sort((a, b) => (_PRIORITY_ORDER[a.state.priority] ?? 4) - (_PRIORITY_ORDER[b.state.priority] ?? 4));
  if (urgent.length === 0) return null;

  return (
    <div style={{ padding: '0 16px', marginBottom: 4 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {urgent.map(ws => {
          const palette = ST.workspace[ws.id] ?? ST.workspaceFallback;
          const blocker = ws.state.blockers[0];
          const isDown  = ws.state.priority === 'critical';
          return (
            <div key={ws.id} style={{
              padding:         '10px 12px',
              borderRadius:    ST.radius.md,
              backgroundColor: isDown ? ST.status.downBg : ST.status.degradedBg,
              border:          `1px solid ${isDown ? ST.status.downBorder : ST.status.degradedBorder}`,
              borderLeft:      `3px solid ${isDown ? ST.status.down : ST.status.degraded}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: blocker ? 5 : 0 }}>
                <span style={{ fontSize: 11 }}>{ws.emoji}</span>
                <span style={{
                  fontSize:   11,
                  fontWeight: 600,
                  color:      palette.icon,
                  fontFamily: ST.font.sans,
                  flex:       1,
                }}>
                  {ws.name.replace(' Workspace', '')}
                </span>
                <span style={{
                  fontSize:        8,
                  fontWeight:      700,
                  letterSpacing:   '0.08em',
                  color:           isDown ? ST.status.down : ST.status.degraded,
                  backgroundColor: isDown ? ST.status.downBg : ST.status.degradedBg,
                  border:          `1px solid ${isDown ? ST.status.downBorder : ST.status.degradedBorder}`,
                  borderRadius:    ST.radius.xs,
                  padding:         '2px 5px',
                  flexShrink:      0,
                }}>
                  {ws.state.priority.toUpperCase()}
                </span>
              </div>
              {blocker && (
                <div style={{
                  fontSize:        10,
                  color:           ST.text.muted,
                  fontFamily:      ST.font.sans,
                  lineHeight:      1.5,
                  marginBottom:    5,
                  overflow:        'hidden',
                  display:         '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>
                  {blocker.replace(/🚨\s?/g, '')}
                </div>
              )}
              <div style={{
                fontSize:        10,
                color:           ST.text.gold,
                fontFamily:      ST.font.sans,
                lineHeight:      1.5,
                overflow:        'hidden',
                display:         '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
              }}>
                → {ws.state.nextAction}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Phase 6: Decisions panel — redesigned default rail ────────────────────────

function DecisionsPanel({
  decisions,
  workspaces,
}: {
  decisions:  CeoDecision[];
  workspaces: Workspace[];
}) {
  const { ST } = useStudioTheme();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggle = (id: string) => setExpandedId(p => (p === id ? null : id));

  // Recent activity: sort by lastUpdated desc, take top 4
  const recentActivity = [...workspaces]
    .sort((a, b) => b.state.lastUpdated.localeCompare(a.state.lastUpdated))
    .slice(0, 4);

  return (
    <div>
      {/* Header */}
      {(() => {
        const criticalCount = workspaces.filter(w => w.state.priority === 'critical').length;
        const blockedCount  = workspaces.filter(w => w.state.blockers.length > 0).length;
        const situationText = criticalCount > 0
          ? `${criticalCount} workspace${criticalCount > 1 ? 's' : ''} need${criticalCount > 1 ? '' : 's'} immediate attention`
          : blockedCount > 0
          ? `${blockedCount} workspace${blockedCount > 1 ? 's' : ''} waiting on external action`
          : 'Studio is operational';
        const situationColor = criticalCount > 0 ? ST.status.down
          : blockedCount > 0 ? ST.status.degraded
          : ST.status.healthy;
        return (
          <div style={{
            padding:         '20px 16px 16px',
            borderBottom:    `1px solid ${ST.border.subtle}`,
            backgroundImage: ST.gradient.hero,
          }}>
            <div style={{
              fontSize:   10,
              color:      situationColor,
              fontFamily: ST.font.sans,
              fontWeight: 500,
              textShadow: `0 0 8px ${situationColor}40`,
            }}>
              {situationText}
            </div>
          </div>
        );
      })()}

      {/* Needs attention — blocked / critical workspaces */}
      <div style={{ paddingTop: 14 }}>
        <BlockersDigest workspaces={workspaces} />
      </div>

      {/* Recent Activity */}
      <div style={{ padding: '0 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {recentActivity.map(ws => {
            const palette = ST.workspace[ws.id] ?? ST.workspaceFallback;
            return (
              <div key={ws.id} style={{
                padding:         '10px 12px',
                borderRadius:    ST.radius.md,
                backgroundColor: `${palette.icon}07`,
                border:          `1px solid ${palette.icon}18`,
                borderLeft:      `4px solid ${palette.icon}75`,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                }}>
                  <span style={{ fontSize: 12 }}>{ws.emoji}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: palette.icon,
                    fontFamily: ST.font.sans,
                    textShadow: `0 0 10px ${palette.icon}40`,
                  }}>
                    {ws.name.replace(' Workspace', '')}
                  </span>
                  <span style={{
                    fontSize: 9, color: ST.text.disabled, fontFamily: ST.font.mono, marginLeft: 'auto',
                  }}>
                    {freshness(ws.state.lastUpdated).replace('Updated ', '')}
                  </span>
                </div>
                <div style={{
                  fontSize:        10, color: ST.text.muted, fontFamily: ST.font.sans, lineHeight: 1.55,
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {ws.state.lastWin}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Divider style={{ margin: '16px 16px' }} />

      {decisions.length > 0 && (
        <div style={{ padding: '0 12px 20px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 10, padding: '0 4px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13 }}>⚖️</span>
              <span style={{
                fontSize: 12, fontWeight: 600, color: ST.text.secondary, fontFamily: ST.font.heading,
              }}>
                Pending Decisions
              </span>
            </div>
            <div style={{
              width: 18, height: 18, borderRadius: ST.radius.full,
              backgroundColor: ST.status.degradedBg, border: `1px solid ${ST.status.degradedBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700, color: ST.status.degraded, fontFamily: ST.font.sans,
            }}>
              {decisions.length}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {decisions.map(d => (
              <DecisionItem
                key={d.id}
                decision={d}
                isExpanded={expandedId === d.id}
                onToggle={() => toggle(d.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Brief question block ──────────────────────────────────────────────────────

function BriefQuestion({
  icon,
  question,
  children,
  accentColor,
  ST,
}: {
  icon:        string;
  question:    string;
  children:    React.ReactNode;
  accentColor: string;
  ST:          StudioTokens;
}) {
  return (
    <div style={{ marginBottom: ST.space[4] }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: ST.space[2] }}>
        <span style={{ fontSize: 11 }}>{icon}</span>
        <span style={{
          fontSize:      9,
          fontWeight:    700,
          fontFamily:    ST.font.sans,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
          color:         accentColor,
          textShadow:    `0 0 10px ${accentColor}50`,
        }}>
          {question}
        </span>
      </div>
      {children}
    </div>
  );
}

// ── Phase 6: Workspace detail — premium brief ─────────────────────────────────

function WorkspaceDetail({
  workspace: ws,
  onBack,
}: {
  workspace: Workspace;
  onBack:    () => void;
}) {
  const { ST } = useStudioTheme();
  const palette = ST.workspace[ws.id] ?? ST.workspaceFallback;
  const hc      = healthColor(ws.state.health, ST);
  const kc      = knowledgeColor(ws.state.knowledgeStatus, ST);
  const s       = ws.state;

  return (
    <div>
      {/* Header */}
      <div style={{
        padding:         '16px 16px 14px',
        borderBottom:    `1px solid ${ST.border.subtle}`,
        backgroundImage: ST.gradient.hero,
      }}>
        <button
          onClick={onBack}
          style={{
            display:         'flex', alignItems: 'center', gap: 5,
            fontSize:        11, color: ST.text.muted, backgroundColor: 'transparent',
            border:          'none', cursor: 'pointer', fontFamily: ST.font.sans,
            marginBottom:    ST.space[3], padding: 0, letterSpacing: '0.01em',
          }}
        >
          ← Back
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width:           40, height: 40, borderRadius: ST.radius.md,
            backgroundColor: palette.bg,
            border:          `1px solid ${palette.icon}40`,
            display:         'flex', alignItems: 'center', justifyContent: 'center',
            fontSize:        20, flexShrink: 0,
            boxShadow:       `0 0 20px ${palette.icon}30`,
          }}>
            {ws.emoji}
          </div>
          <div>
            <div style={{
              fontSize:             14,
              fontWeight:           700,
              fontFamily:           ST.font.heading,
              lineHeight:           1.2,
              letterSpacing:        '-0.01em',
              background:           `linear-gradient(135deg, ${palette.icon} 0%, ${ST.text.primary} 60%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor:  'transparent',
              backgroundClip:       'text',
            }}>
              {ws.name.replace(' Workspace', '')}
            </div>
            <div style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans, marginTop: 2 }}>
              {freshness(s.lastUpdated)}
            </div>
          </div>
        </div>

        {/* Status row */}
        <div style={{
          display:         'flex', alignItems: 'center', gap: 12, marginTop: ST.space[3],
          padding:         '8px 12px', borderRadius: ST.radius.md,
          backgroundColor: ST.bg.overlay, border: `1px solid ${ST.border.subtle}`,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <StatusDot color={hc} size={5} animate={healthToAnimation(s.health)} />
            <span style={{
              fontSize: 10, fontWeight: 600, fontFamily: ST.font.sans,
              color: hc, textShadow: `0 0 8px ${hc}60`,
            }}>
              {healthLabel(s.health)}
            </span>
          </span>
          <span style={{ fontSize: 10, color: ST.border.default }}>·</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <StatusDot color={kc} size={5} />
            <span style={{ fontSize: 10, color: ST.text.muted, fontFamily: ST.font.sans }}>
              {knowledgeLabel(s.knowledgeStatus)}
            </span>
          </span>
          <span style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans, marginLeft: 'auto' }}>
            Sync {s.lastSync}
          </span>
        </div>
      </div>

      {/* Protocols */}
      <div style={{ padding: '16px 16px 0' }}>
        <GlassCard style={{ padding: `${ST.space[3]}px` }} accentColor={palette.icon}>
          <div style={{
            fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase',
            color: ST.text.disabled, fontFamily: ST.font.sans, fontWeight: 600, marginBottom: ST.space[2],
          }}>
            Protocols
          </div>
          <div style={{ marginBottom: ST.space[2] }}>
            <CopyButton text={generateContinueProtocol(ws)} label="Continue Protocol" primary />
          </div>
          <CopyButton text={generateSyncProtocol(ws)} label="Sync Protocol" />
        </GlassCard>
      </div>

      <Divider style={{ margin: '16px 16px' }} />

      {/* Engineering Brief */}
      <div style={{ padding: '0 16px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: ST.space[2], marginBottom: ST.space[4],
        }}>
          <span style={{ fontSize: 13 }}>📋</span>
          <span style={{
            fontSize: 12, fontWeight: 700, fontFamily: ST.font.heading,
            letterSpacing: '-0.01em', color: ST.text.secondary,
          }}>
            Engineering Brief
          </span>
        </div>

        <BriefQuestion icon="🎯" question="Why am I here?" accentColor={palette.icon} ST={ST}>
          <div style={{
            fontSize: 12, color: ST.text.secondary, fontFamily: ST.font.sans, lineHeight: 1.65,
          }}>
            {s.currentMission}
          </div>
          {s.lastWin && (
            <div style={{
              marginTop:       ST.space[2], fontSize: 10, color: ST.text.muted, fontFamily: ST.font.sans,
              lineHeight:      1.5, padding: `${ST.space[2]}px`, borderRadius: ST.radius.sm,
              backgroundColor: ST.bg.overlay, border: `1px solid ${ST.border.subtle}`,
            }}>
              ↑ {s.lastWin}
            </div>
          )}
        </BriefQuestion>

        <BriefQuestion
          icon="⚠"
          question="What's blocking?"
          accentColor={s.blockers.length > 0 ? ST.status.degraded : ST.status.healthy}
          ST={ST}
        >
          {s.blockers.length === 0 && s.pendingDecisions.length === 0 ? (
            <div style={{
              fontSize: 11, color: ST.status.healthy, fontFamily: ST.font.sans,
              textShadow: `0 0 8px ${ST.status.healthy}50`,
            }}>
              Nothing blocking
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {s.blockers.map((b, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 6, alignItems: 'flex-start',
                  fontSize: 11, color: ST.text.muted, fontFamily: ST.font.sans, lineHeight: 1.5,
                  padding: `${ST.space[2]}px`, borderRadius: ST.radius.sm,
                  backgroundColor: ST.status.downBg, border: `1px solid ${ST.status.downBorder}`,
                }}>
                  <span style={{ color: ST.status.down, flexShrink: 0 }}>⚠</span>
                  <span>{b}</span>
                </div>
              ))}
              {s.pendingDecisions.map((d, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 6, alignItems: 'flex-start',
                  fontSize: 11, color: ST.text.muted, fontFamily: ST.font.sans, lineHeight: 1.5,
                }}>
                  <span style={{ color: ST.text.disabled, flexShrink: 0 }}>?</span>
                  <span>{d}</span>
                </div>
              ))}
            </div>
          )}
        </BriefQuestion>

        <BriefQuestion icon="→" question="What's next?" accentColor={ST.text.gold} ST={ST}>
          <div style={{
            fontSize: 12, color: ST.text.gold, fontFamily: ST.font.sans, lineHeight: 1.6,
            padding: `${ST.space[2]}px ${ST.space[3]}px`, borderRadius: ST.radius.sm,
            backgroundColor: ST.accent.goldSubtle, border: `1px solid ${ST.border.gold}`,
          }}>
            → {s.nextAction}
          </div>
        </BriefQuestion>

        {s.notes && (
          <div style={{ marginBottom: ST.space[4] }}>
            <div style={{
              fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase',
              color: ST.text.disabled, fontFamily: ST.font.sans, fontWeight: 600, marginBottom: 5,
            }}>
              Notes
            </div>
            <div style={{ fontSize: 11, color: ST.text.disabled, fontFamily: ST.font.sans, lineHeight: 1.65 }}>
              {s.notes}
            </div>
          </div>
        )}
      </div>

      <Divider style={{ margin: '0 16px' }} />

      {/* Passport */}
      <div style={{ padding: '12px 16px 24px' }}>
        <div style={{
          fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase',
          color: ST.text.disabled, fontFamily: ST.font.sans, fontWeight: 600, marginBottom: ST.space[2],
        }}>
          Workspace Passport
        </div>
        <div style={{
          padding: ST.space[3], borderRadius: ST.radius.md,
          backgroundColor: ST.bg.overlay, border: `1px solid ${ST.border.subtle}`,
        }}>
          <div style={{
            fontSize: 10, color: ST.text.muted, fontFamily: ST.font.sans,
            lineHeight: 1.5, marginBottom: ST.space[2], wordBreak: 'break-all',
          }}>
            📄 {ws.passportPath}
          </div>
          <div style={{ fontSize: 10, color: ST.text.disabled, fontFamily: ST.font.sans, lineHeight: 1.6 }}>
            Full identity, ownership, and history live in this file. The Continue Protocol
            above loads this passport automatically before starting work.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function RightPanel({
  decisions,
  selectedWorkspace,
  workspaces,
  onBack,
}: {
  decisions:         CeoDecision[];
  selectedWorkspace: Workspace | null;
  workspaces:        Workspace[];
  onBack:            () => void;
}) {
  return (
    <div>
      {selectedWorkspace
        ? <WorkspaceDetail workspace={selectedWorkspace} onBack={onBack} />
        : <DecisionsPanel decisions={decisions} workspaces={workspaces} />
      }
    </div>
  );
}
